import { supabase } from '../firebase/config';

// Product interface matching Firestore structure
export interface Product {
  productId: string;
  vendorId: string;
  categoryId: string;
  name: string;
  description: string;
  shortDescription: string;
  price: {
    current: number;
    original: number;
    currency: string;
    discountPercentage?: number;
  };
  inventory: {
    quantity: number;
    sku: string;
    lowStockThreshold: number;
    isInStock: boolean;
  };
  images: {
    [imageId: string]: {
      url: string;
      alt: string;
      isPrimary: boolean;
      order: number;
    };
  };
  specifications: {
    [key: string]: string | number | boolean;
  };
  variants?: {
    [variantId: string]: {
      name: string;
      price: number;
      quantity: number;
      sku: string;
    };
  };
  shipping: {
    weight: number;
    dimensions: {
      length: number;
      width: number;
      height: number;
    };
    shippingClass: 'standard' | 'express' | 'overnight';
    freeShipping: boolean;
    shippingCost: number;
  };
  tags: string[];
  keywords: string[];
  status: 'draft' | 'active' | 'inactive' | 'archived';
  isFeatured: boolean;
  isTrending: boolean;
  metrics: {
    views: number;
    favorites: number;
    sales: number;
    averageRating: number;
    reviewCount: number;
  };
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
}

// Product form data interface for creating/updating products
export interface ProductFormData {
  name: string;
  description: string;
  shortDescription: string;
  currentPrice: number;
  originalPrice?: number;
  currency: string;
  quantity: number;
  sku: string;
  lowStockThreshold: number;
  weight: number;
  length?: number;
  width?: number;
  height?: number;
  shippingClass: 'standard' | 'express' | 'overnight';
  freeShipping: boolean;
  shippingCost: number;
  tags: string[];
  keywords: string[];
  categoryId: string;
  specifications: { [key: string]: string | number | boolean };
  images?: {
    [imageId: string]: {
      url: string;
      alt: string;
      isPrimary: boolean;
      order: number;
    };
  };
}

class ProductService {
  private readonly tableName = 'products';

  /**
   * Create a new product
   */
  async createProduct(vendorId: string, productData: ProductFormData): Promise<string> {
    try {
      const now = new Date().toISOString();
      const discountPercentage = productData.originalPrice && productData.originalPrice > productData.currentPrice
        ? Math.round(((productData.originalPrice - productData.currentPrice) / productData.originalPrice) * 100)
        : undefined;

      const payload = {
        vendor_id: vendorId,
        category_id: productData.categoryId,
        name: productData.name,
        description: productData.description,
        short_description: productData.shortDescription,
        price: {
          current: productData.currentPrice,
          original: productData.originalPrice || productData.currentPrice,
          currency: productData.currency,
          discountPercentage
        },
        inventory: {
          quantity: productData.quantity,
          sku: productData.sku,
          lowStockThreshold: productData.lowStockThreshold,
          isInStock: productData.quantity > 0
        },
        images: productData.images || {},
        specifications: productData.specifications,
        shipping: {
          weight: productData.weight,
          dimensions: {
            length: productData.length || 0,
            width: productData.width || 0,
            height: productData.height || 0
          },
          shippingClass: productData.shippingClass,
          freeShipping: productData.freeShipping,
          shippingCost: productData.shippingCost
        },
        tags: productData.tags,
        keywords: productData.keywords,
        status: 'draft' as const,
        is_featured: false,
        is_trending: false,
        metrics: {
          views: 0,
          favorites: 0,
          sales: 0,
          averageRating: 0,
          reviewCount: 0
        },
        created_at: now,
        updated_at: now
      };

      const { data, error } = await supabase.from(this.tableName).insert(payload).select('id').single();
      if (error) throw error;
      return data.id;
    } catch (error) {
      console.error('Error creating product:', error);
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: string }).message)
          : 'Failed to create product';
      throw new Error(message);
    }
  }

  /**
   * Update an existing product
   */
  async updateProduct(productId: string, productData: Partial<ProductFormData>): Promise<void> {
    try {
      const { data: currentData, error: currentError } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', productId)
        .single();
      if (currentError) throw currentError;
      const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };

      // Update basic fields
      if (productData.name !== undefined) updateData.name = productData.name;
      if (productData.description !== undefined) updateData.description = productData.description;
      if (productData.shortDescription !== undefined) updateData.short_description = productData.shortDescription;
      if (productData.categoryId !== undefined) updateData.category_id = productData.categoryId;
      if (productData.tags !== undefined) updateData.tags = productData.tags;
      if (productData.keywords !== undefined) updateData.keywords = productData.keywords;
      if (productData.specifications !== undefined) updateData.specifications = productData.specifications;
      if (productData.images !== undefined) updateData.images = productData.images;

      // Update pricing
      if (productData.currentPrice !== undefined || productData.originalPrice !== undefined || productData.currency !== undefined) {
        const currentPrice = productData.currentPrice ?? currentData?.price?.current;
        const originalPrice = productData.originalPrice ?? currentData?.price?.original;
        const currency = productData.currency ?? currentData?.price?.currency;
        
        const discountPercentage = originalPrice && originalPrice > currentPrice
          ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
          : undefined;

        updateData.price = {
          current: currentPrice,
          original: originalPrice,
          currency,
          discountPercentage
        };
      }

      // Update inventory
      if (productData.quantity !== undefined || productData.sku !== undefined || productData.lowStockThreshold !== undefined) {
        updateData.inventory = {
          quantity: productData.quantity ?? currentData?.inventory?.quantity,
          sku: productData.sku ?? currentData?.inventory?.sku,
          lowStockThreshold: productData.lowStockThreshold ?? currentData?.inventory?.lowStockThreshold,
          isInStock: (productData.quantity ?? currentData?.inventory?.quantity) > 0
        };
      }

      // Update shipping
      if (productData.weight !== undefined || productData.length !== undefined || productData.width !== undefined ||
          productData.height !== undefined || productData.shippingClass !== undefined || 
          productData.freeShipping !== undefined || productData.shippingCost !== undefined) {
        updateData.shipping = {
          weight: productData.weight ?? currentData?.shipping?.weight,
          dimensions: {
            length: productData.length ?? currentData?.shipping?.dimensions?.length ?? 0,
            width: productData.width ?? currentData?.shipping?.dimensions?.width ?? 0,
            height: productData.height ?? currentData?.shipping?.dimensions?.height ?? 0
          },
          shippingClass: productData.shippingClass ?? currentData?.shipping?.shippingClass,
          freeShipping: productData.freeShipping ?? currentData?.shipping?.freeShipping,
          shippingCost: productData.shippingCost ?? currentData?.shipping?.shippingCost
        };
      }

      const { error } = await supabase.from(this.tableName).update(updateData).eq('id', productId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating product:', error);
      throw new Error('Failed to update product');
    }
  }

  /**
   * Delete a product
   */
  async deleteProduct(productId: string): Promise<void> {
    try {
      const { error } = await supabase.from(this.tableName).delete().eq('id', productId);
      if (error) throw error;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw new Error('Failed to delete product');
    }
  }

  /**
   * Get a single product by ID
   */
  async getProduct(productId: string): Promise<Product | null> {
    try {
      const { data, error } = await supabase.from(this.tableName).select('*').eq('id', productId).maybeSingle();
      if (error) throw error;
      if (data) {
        return {
          productId: data.id,
          vendorId: data.vendor_id,
          categoryId: data.category_id,
          name: data.name,
          description: data.description,
          shortDescription: data.short_description,
          price: data.price,
          inventory: data.inventory,
          images: data.images || {},
          specifications: data.specifications || {},
          shipping: data.shipping,
          tags: data.tags || [],
          keywords: data.keywords || [],
          status: data.status,
          isFeatured: data.is_featured,
          isTrending: data.is_trending,
          metrics: data.metrics,
          createdAt: data.created_at ? new Date(data.created_at) : new Date(),
          updatedAt: data.updated_at ? new Date(data.updated_at) : new Date(),
          publishedAt: data.published_at ? new Date(data.published_at) : undefined,
        } as Product;
      }
      return null;
    } catch (error) {
      console.error('Error getting product:', error);
      throw new Error('Failed to get product');
    }
  }

  /**
   * Get all products for a specific vendor
   */
  async getVendorProducts(vendorId: string, limitCount: number = 50): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('vendor_id', vendorId)
        .order('created_at', { ascending: false })
        .limit(limitCount);
      if (error) throw error;
      return (data || []).map((row) => ({
        productId: row.id,
        vendorId: row.vendor_id,
        categoryId: row.category_id,
        name: row.name,
        description: row.description,
        shortDescription: row.short_description,
        price: row.price,
        inventory: row.inventory,
        images: row.images || {},
        specifications: row.specifications || {},
        shipping: row.shipping,
        tags: row.tags || [],
        keywords: row.keywords || [],
        status: row.status,
        isFeatured: row.is_featured,
        isTrending: row.is_trending,
        metrics: row.metrics,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
        publishedAt: row.published_at ? new Date(row.published_at) : undefined,
      })) as Product[];
    } catch (error) {
      console.error('Error getting vendor products:', error);
      throw new Error('Failed to get vendor products');
    }
  }

  /**
   * Get products by category
   */
  async getProductsByCategory(categoryId: string, limitCount: number = 50): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('category_id', categoryId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(limitCount);
      if (error) throw error;
      return (data || []).map((row) => ({ ...row, productId: row.id, vendorId: row.vendor_id, categoryId: row.category_id, shortDescription: row.short_description, isFeatured: row.is_featured, isTrending: row.is_trending, createdAt: new Date(row.created_at), updatedAt: new Date(row.updated_at), publishedAt: row.published_at ? new Date(row.published_at) : undefined })) as Product[];
    } catch (error) {
      console.error('Error getting products by category:', error);
      throw new Error('Failed to get products by category');
    }
  }

  /**
   * Get featured products
   */
  async getFeaturedProducts(limitCount: number = 20): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('is_featured', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(limitCount);
      if (error) throw error;
      return (data || []).map((row) => ({ ...row, productId: row.id, vendorId: row.vendor_id, categoryId: row.category_id, shortDescription: row.short_description, isFeatured: row.is_featured, isTrending: row.is_trending, createdAt: new Date(row.created_at), updatedAt: new Date(row.updated_at), publishedAt: row.published_at ? new Date(row.published_at) : undefined })) as Product[];
    } catch (error) {
      console.error('Error getting featured products:', error);
      throw new Error('Failed to get featured products');
    }
  }

  /**
   * Update product status
   */
  async updateProductStatus(productId: string, status: 'draft' | 'active' | 'inactive' | 'archived'): Promise<void> {
    try {
      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      // Set publishedAt when status changes to active
      if (status === 'active') {
        updateData.published_at = new Date().toISOString();
      }

      const { error } = await supabase.from(this.tableName).update(updateData).eq('id', productId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating product status:', error);
      throw new Error('Failed to update product status');
    }
  }

  /**
   * Update product metrics
   */
  async updateProductMetrics(productId: string, metrics: Partial<Product['metrics']>): Promise<void> {
    try {
      const { data: currentData, error: fetchError } = await supabase
        .from(this.tableName)
        .select('metrics')
        .eq('id', productId)
        .single();
      if (fetchError) throw fetchError;
      
      const updatedMetrics = {
        ...currentData?.metrics,
        ...metrics
      };

      const { error } = await supabase.from(this.tableName).update({
        metrics: updatedMetrics,
        updated_at: new Date().toISOString()
      }).eq('id', productId);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating product metrics:', error);
      throw new Error('Failed to update product metrics');
    }
  }

  /**
   * Search products by name or description
   */
  async searchProducts(searchTerm: string, limitCount: number = 50): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('status', 'active')
        .order('name')
        .limit(limitCount);
      if (error) throw error;

      const products: Product[] = [];
      (data || []).forEach((row) => {
        const product = {
          ...row,
          productId: row.id,
          vendorId: row.vendor_id,
          categoryId: row.category_id,
          shortDescription: row.short_description,
          isFeatured: row.is_featured,
          isTrending: row.is_trending,
          createdAt: new Date(row.created_at),
          updatedAt: new Date(row.updated_at),
          publishedAt: row.published_at ? new Date(row.published_at) : undefined
        } as Product;

        // Basic search filtering
        const searchLower = searchTerm.toLowerCase();
        if (
          product.name.toLowerCase().includes(searchLower) ||
          product.description.toLowerCase().includes(searchLower) ||
          product.shortDescription.toLowerCase().includes(searchLower) ||
          product.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
          product.keywords.some(keyword => keyword.toLowerCase().includes(searchLower))
        ) {
          products.push(product);
        }
      });

      return products.slice(0, limitCount);
    } catch (error) {
      console.error('Error searching products:', error);
      throw new Error('Failed to search products');
    }
  }

  /**
   * Get all active products for homepage display
   */
  async getAllActiveProducts(limitCount: number = 100): Promise<Product[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(limitCount);
      if (error) throw error;
      return (data || []).map((row) => ({ ...row, productId: row.id, vendorId: row.vendor_id, categoryId: row.category_id, shortDescription: row.short_description, isFeatured: row.is_featured, isTrending: row.is_trending, createdAt: new Date(row.created_at), updatedAt: new Date(row.updated_at), publishedAt: row.published_at ? new Date(row.published_at) : undefined })) as Product[];
    } catch (error) {
      console.error('Error getting all active products:', error);
      throw new Error('Failed to get all active products');
    }
  }

  /**
   * Get products with pagination
   */
  async getProductsPaginated(
    lastDoc?: any, 
    limitCount: number = 20,
    filters?: {
      categoryId?: string;
      vendorId?: string;
      status?: string;
      isFeatured?: boolean;
    }
  ): Promise<{ products: Product[]; lastDoc: any }> {
    try {
      let queryBuilder = supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limitCount);
      if (filters?.categoryId) queryBuilder = queryBuilder.eq('category_id', filters.categoryId);
      if (filters?.vendorId) queryBuilder = queryBuilder.eq('vendor_id', filters.vendorId);
      if (filters?.status) queryBuilder = queryBuilder.eq('status', filters.status);
      if (filters?.isFeatured !== undefined) queryBuilder = queryBuilder.eq('is_featured', filters.isFeatured);
      if (lastDoc) queryBuilder = queryBuilder.lt('created_at', lastDoc);
      const { data, error } = await queryBuilder;
      if (error) throw error;
      const products = (data || []).map((row) => ({ ...row, productId: row.id, vendorId: row.vendor_id, categoryId: row.category_id, shortDescription: row.short_description, isFeatured: row.is_featured, isTrending: row.is_trending, createdAt: new Date(row.created_at), updatedAt: new Date(row.updated_at), publishedAt: row.published_at ? new Date(row.published_at) : undefined })) as Product[];
      const newLastDoc = products.length ? products[products.length - 1].createdAt.toISOString() : null;
      return { products, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error getting paginated products:', error);
      throw new Error('Failed to get paginated products');
    }
  }
}

export const productService = new ProductService();
